import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE } from '../config';
import InterviewWizard from './InterviewWizard';

/* ─── Minimal SVG Icons ──────────────────────────────────────────── */
const WingIcon = (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2251FF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" />
  </svg>
);
const LockIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const CheckCircleIcon = (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const AlertIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/*
  InterviewPortal — Standalone interview page for external users.

  Screen state machine:
    loading → welcome → onboarding → interview → thankyou
                │           │            │
                └──(error)──┘            │
                       │                 │
                       └─────(401)───────┘ → welcome (re-auth prompt)

  onboarding sub-states: onboardingStep 1 → 2 → 3 → setScreen('interview')
*/

export default function InterviewPortal() {
  const { inviteCode } = useParams();

  // Screen state
  const [screen, setScreen] = useState('loading'); // loading | welcome | onboarding | interview | thankyou | error
  const [inviteInfo, setInviteInfo] = useState(null);
  const [portalToken, setPortalToken] = useState(null);
  const [error, setError] = useState('');

  // Onboarding sub-step (1, 2, or 3)
  const [onboardingStep, setOnboardingStep] = useState(1);

  // Passcode form
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  // Load invite info on mount
  useEffect(() => {
    if (!inviteCode) {
      setError('No invite code provided.');
      setScreen('error');
      return;
    }
    fetchInviteInfo();
  }, [inviteCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInviteInfo = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/interview/portal/${inviteCode}/info`);
      let data = {};
      try {
        data = await res.json();
      } catch {
        // Non-JSON response
      }
      if (!res.ok) {
        // Show specific error messages for different statuses
        if (res.status === 404) {
          setError('This invite link is not valid. Please check the link and try again.');
        } else {
          setError(data.detail || 'This invitation is no longer available.');
        }
        setScreen('error');
        return;
      }
      setInviteInfo(data);
      if (data.status === 'completed') {
        setScreen('thankyou');
      } else if (!data.valid) {
        setError('This interview invitation has expired. Please contact your administrator for a new link.');
        setScreen('error');
      } else {
        setScreen('welcome');
      }
    } catch (err) {
      setError('Unable to load invite information. Please check your connection and try again.');
      setScreen('error');
    }
  };

  // Handle passcode submission
  const handleValidate = async (e) => {
    e.preventDefault();
    const trimmedPasscode = passcode.trim().toUpperCase();
    if (!trimmedPasscode) {
      setPasscodeError('Please enter your passcode.');
      return;
    }
    if (trimmedPasscode.length !== 6) {
      setPasscodeError('Passcode is 6 characters.');
      return;
    }
    if (!name.trim()) {
      setPasscodeError('Please enter your name.');
      return;
    }
    if (!email.trim()) {
      setPasscodeError('Please enter your email.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setPasscodeError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    setPasscodeError('');

    try {
      const res = await fetch(`${API_BASE}/api/interview/portal/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invite_code: inviteCode,
          passcode: trimmedPasscode,
          name: name.trim(),
          email: email.trim(),
        }),
      });

      // Parse response body safely — error responses may not be JSON
      let data = {};
      try {
        data = await res.json();
      } catch {
        // Non-JSON response — use status code only for error handling
      }

      if (!res.ok) {
        if (res.status === 429) {
          setPasscodeError('Too many attempts. Please wait a few minutes and try again.');
        } else if (res.status === 401) {
          setPasscodeError('Incorrect passcode. Please check and try again.');
        } else if (res.status === 403) {
          setError(data.detail || 'This invitation is no longer available.');
          setScreen('error');
        } else {
          setPasscodeError(data.detail || 'Validation failed. Please try again.');
        }
        setSubmitting(false);
        return;
      }

      // Success — verify we got a token before advancing
      if (!data.token) {
        setPasscodeError('Validation succeeded but no token received. Please try again.');
        setSubmitting(false);
        return;
      }

      setPortalToken(data.token);
      setOnboardingStep(1);
      setScreen('onboarding');
    } catch (err) {
      setPasscodeError('Connection error. Please check your network and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle interview completion
  const handleInterviewComplete = useCallback(() => {
    setScreen('thankyou');
  }, []);

  // Handle 401 from InterviewWizard (token expired)
  const handleAuthExpired = useCallback(() => {
    setPortalToken(null);
    setPasscode('');
    setPasscodeError('Your session has expired. Please enter your passcode to continue.');
    setScreen('welcome');
  }, []);

  // ─── Render ───────────────────────────────────────────────────────

  if (screen === 'loading') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  if (screen === 'error') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            {WingIcon}
            <h1 style={styles.brand}>Wingman</h1>
          </div>
          <div style={styles.errorBox}>
            <span style={{ marginRight: 8, flexShrink: 0 }}>{AlertIcon}</span>
            <span>{error}</span>
          </div>
          <p style={styles.helpText}>
            If you believe this is an error, please contact the person who sent you this link.
          </p>
        </div>
      </div>
    );
  }

  if (screen === 'thankyou') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            {WingIcon}
            <h1 style={styles.brand}>Wingman</h1>
          </div>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            {CheckCircleIcon}
            <h2 style={{ ...styles.title, marginTop: 16 }}>Thank You!</h2>
            <p style={styles.subtitle}>
              Your interview has been completed successfully. Your insights have been recorded
              and will help inform better procurement decisions.
            </p>
            <p style={styles.helpText}>
              You can close this window now. If you have any questions, please contact your administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'welcome') {
    const passcodeReady = passcode.trim().length === 6;
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            {WingIcon}
            <h1 style={styles.brand}>Wingman</h1>
          </div>
          <h2 style={styles.title}>
            {inviteInfo?.first_name
              ? `Welcome, ${inviteInfo.first_name}`
              : 'Welcome'}
          </h2>
          <p style={styles.subtitle}>
            You've been invited to share your procurement knowledge through a brief
            conversational interview. Your insights will help build better intelligence
            for your team.
          </p>
          <p style={styles.infoText}>
            The interview typically takes 20-30 minutes and covers several topics.
            You can pause at any time and come back later using the same link and passcode.
          </p>

          <form onSubmit={handleValidate} style={styles.form}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Your Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                style={styles.input}
                autoComplete="name"
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Your Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                style={styles.input}
                autoComplete="email"
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {LockIcon} Passcode
                </span>
              </label>
              <input
                type="text"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value.toUpperCase())}
                placeholder="Enter your 6-character passcode"
                style={{ ...styles.input, letterSpacing: '0.15em', fontFamily: 'monospace', fontSize: 16 }}
                maxLength={6}
                autoComplete="off"
                autoFocus
              />
              {passcode.length > 0 && passcode.length < 6 && (
                <div style={{ color: '#6B7280', fontSize: 12, marginTop: 4 }}>
                  Passcode is 6 characters
                </div>
              )}
            </div>

            {passcodeError && (
              <div style={styles.errorText}>
                {passcodeError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !passcodeReady}
              style={{
                ...styles.submitButton,
                opacity: (submitting || !passcodeReady) ? 0.6 : 1,
                cursor: (submitting || !passcodeReady) ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Verifying...' : 'Start Interview'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (screen === 'onboarding') {
    /* ─── Helper: renders one numbered instruction step ──────────── */
    const renderInstructionStep = (number, text, visual) => (
      <div style={styles.instructionStep}>
        <div style={styles.stepNumber}>{number}</div>
        <div style={styles.stepContent}>
          <p style={styles.stepText}>{text}</p>
          {visual && <div style={styles.stepVisual}>{visual}</div>}
        </div>
      </div>
    );

    /* ─── Static visual replicas for Screen 2 ────────────────────── */
    const micIcon = (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
      </svg>
    );
    const stopIcon = (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="16" height="16" rx="2" />
      </svg>
    );
    const spinnerIcon = (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ animation: 'spin 0.8s linear infinite' }}>
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
      </svg>
    );

    // Static waveform bars — varying heights to simulate mid-speech
    const waveformHeights = [25, 55, 40, 70, 85, 60, 45, 90, 65, 35, 75, 50, 80, 40, 60, 85, 30, 70, 55, 45];
    const staticWaveform = (
      <div style={styles.staticWaveform}>
        {waveformHeights.map((h, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: `${h}%`,
              minHeight: 4,
              borderRadius: '3px 3px 0 0',
              backgroundColor: '#2251FF',
            }}
          />
        ))}
      </div>
    );

    const recordButton = (
      <div style={styles.replicaBtn}>
        {micIcon}
        <span>Record</span>
      </div>
    );
    const stopButton = (
      <div style={{ ...styles.replicaBtn, borderColor: '#DC2626', color: '#DC2626', backgroundColor: '#FEF2F2' }}>
        {stopIcon}
        <span>Stop</span>
      </div>
    );
    const transcribingIndicator = (
      <div style={{ ...styles.replicaBtn, opacity: 0.8, borderColor: '#E2E5EA', color: '#6B7280' }}>
        {spinnerIcon}
        <span>Transcribing...</span>
      </div>
    );

    /* ─── Dot indicator ──────────────────────────────────────────── */
    const dotIndicator = (
      <div style={styles.dotIndicator}>
        {[1, 2, 3].map((step) => (
          <div
            key={step}
            style={{
              ...styles.dot,
              backgroundColor: step === onboardingStep ? '#2251FF' : '#E2E5EA',
            }}
          />
        ))}
      </div>
    );

    return (
      <div style={styles.container}>
        <div style={{ ...styles.card, maxWidth: 540 }}>
          <div style={styles.header}>
            {WingIcon}
            <h1 style={styles.brand}>Wingman</h1>
          </div>
          {dotIndicator}

          {onboardingStep === 1 && (
            <div>
              <h2 style={styles.title}>Welcome to Your Wingman Interview</h2>
              <p style={styles.subtitle}>
                This is a guided conversation with an AI that will ask you questions based on your role
                and expertise — covering the suppliers, categories, and accounts you work with day to day.
                It takes about 30 minutes.
              </p>
              <p style={styles.subtitle}>
                Be specific and honest. Share what's working well, what's not, and where you see opportunities.
                The goal is to capture what you actually know from experience — your insights are what make this
                platform valuable for the whole team.
              </p>
              <button
                onClick={() => setOnboardingStep(2)}
                style={styles.submitButton}
              >
                Next
              </button>
              <div style={{ ...styles.infoText, marginTop: 16 }}>
                If you need to step away at any point, your progress is saved. Just come back to the same
                link to pick up where you left off.
              </div>
            </div>
          )}

          {onboardingStep === 2 && (
            <div>
              <h2 style={styles.title}>Speaking Your Answers is Recommended</h2>
              <p style={styles.subtitle}>
                You can speak your answers or type them. Speaking is encouraged — it produces more detailed,
                natural responses and is usually faster.
              </p>
              <div style={styles.instructionList}>
                {renderInstructionStep(1, 'Tap Record and allow microphone access if your browser asks', recordButton)}
                {renderInstructionStep(2, 'Speak naturally — the audio bars confirm it\'s picking you up', staticWaveform)}
                {renderInstructionStep(3, 'Tap Stop when you\'re done', stopButton)}
                {renderInstructionStep(4, 'Wait a few seconds for your answer to transcribe', transcribingIndicator)}
                {renderInstructionStep(5, 'Review your answer — no need to fix spelling or grammar, only correct if the meaning is wrong', null)}
              </div>
              <div style={{ ...styles.infoText, marginBottom: 16 }}>
                Prefer typing? No problem — just type your answer in the text box and submit.
                Both options work equally well.
              </div>
              <div style={styles.buttonRow}>
                <button
                  onClick={() => setOnboardingStep(1)}
                  style={styles.backButton}
                >
                  Back
                </button>
                <button
                  onClick={() => setOnboardingStep(3)}
                  style={styles.submitButton}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {onboardingStep === 3 && (
            <div>
              <h2 style={styles.title}>You're All Set</h2>
              <p style={styles.subtitle}>
                Every question matters. If you don't know the answer to something, just say so and the AI will
                move on. There are no wrong answers.
              </p>
              <div style={styles.buttonRow}>
                <button
                  onClick={() => setOnboardingStep(2)}
                  style={styles.backButton}
                >
                  Back
                </button>
                <button
                  onClick={() => setScreen('interview')}
                  style={styles.submitButton}
                >
                  Start Interview
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // screen === 'interview'
  return (
    <div style={styles.interviewContainer}>
      <div style={styles.interviewHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {WingIcon}
          <span style={styles.headerBrand}>Wingman Interview</span>
        </div>
      </div>
      <div style={styles.interviewBody}>
        <InterviewWizard
          portalMode={true}
          portalToken={portalToken}
          onComplete={handleInterviewComplete}
          onAuthExpired={handleAuthExpired}
        />
      </div>
    </div>
  );
}

/* ─── Inline styles (no external CSS dependency) ─────────────────── */
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8F9FB',
    padding: 20,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #E2E5EA',
    padding: 40,
    maxWidth: 480,
    width: '100%',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 24,
  },
  brand: {
    fontSize: 20,
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    color: '#111827',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 1.6,
    margin: '0 0 16px 0',
  },
  infoText: {
    fontSize: 13,
    color: '#8A94A6',
    lineHeight: 1.5,
    margin: '0 0 24px 0',
    padding: '12px 16px',
    backgroundColor: '#F8F9FB',
    borderRadius: 8,
    border: '1px solid #F0F1F3',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#6B7280',
  },
  input: {
    padding: '10px 14px',
    fontSize: 14,
    borderRadius: 8,
    border: '1px solid #E2E5EA',
    outline: 'none',
    transition: 'border-color 0.2s',
    backgroundColor: '#FFFFFF',
  },
  errorText: {
    fontSize: 13,
    color: '#DC2626',
    padding: '8px 12px',
    backgroundColor: '#FEF2F2',
    borderRadius: 6,
    border: '1px solid #FECACA',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '16px',
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    border: '1px solid #FECACA',
    color: '#DC2626',
    fontSize: 14,
    marginBottom: 16,
  },
  helpText: {
    fontSize: 13,
    color: '#8A94A6',
    lineHeight: 1.5,
    margin: 0,
  },
  submitButton: {
    padding: '12px 24px',
    fontSize: 14,
    fontWeight: 600,
    color: '#FFFFFF',
    backgroundColor: '#2251FF',
    border: 'none',
    borderRadius: 8,
    marginTop: 8,
  },
  loadingText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #E2E5EA',
    borderTopColor: '#2251FF',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 12px',
  },
  interviewContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F8F9FB',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  interviewHeader: {
    padding: '12px 24px',
    borderBottom: '1px solid #E2E5EA',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerBrand: {
    fontSize: 15,
    fontWeight: 600,
    color: '#111827',
  },
  interviewBody: {
    flex: 1,
    padding: '24px',
    maxWidth: 900,
    margin: '0 auto',
    width: '100%',
  },
  // ─── Onboarding styles ──────────────────────────────────────────
  dotIndicator: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 20,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    transition: 'background-color 0.2s',
  },
  buttonRow: {
    display: 'flex',
    gap: 12,
    marginTop: 8,
  },
  backButton: {
    padding: '12px 24px',
    fontSize: 14,
    fontWeight: 600,
    color: '#6B7280',
    backgroundColor: 'transparent',
    border: '1px solid #E2E5EA',
    borderRadius: 8,
    cursor: 'pointer',
  },
  instructionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 20,
  },
  instructionStep: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 16px',
    backgroundColor: '#F8F9FB',
    borderRadius: 8,
    border: '1px solid #F0F1F3',
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: '#2251FF',
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  stepContent: {
    flex: 1,
    minWidth: 0,
  },
  stepText: {
    fontSize: 13,
    color: '#111827',
    lineHeight: 1.5,
    margin: 0,
  },
  stepVisual: {
    marginTop: 8,
  },
  replicaBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    border: '1px solid #E2E5EA',
    color: '#6B7280',
    backgroundColor: '#FFFFFF',
  },
  staticWaveform: {
    display: 'flex',
    gap: 3,
    height: 48,
    alignItems: 'flex-end',
  },
};
